// src/components/ProtectedRoute.js
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { RMS_SERVICE_USER_TYPES } from '../constants/userTypes';
import { rmsServiceUserRedirect } from '../rms/utils/serviceUserRouteGuard';

export default function ProtectedRoute({ children, allowedRoles }) {
  const { 
    isAuthenticated, 
    isLoading, 
    claims,
    user,
    error,
    errorType,
    isEmailVerified,
    isMFAEnrolled,
    retryAuth,
    // clearError
  } = useAuth();
  const location = useLocation();

  // Helper function to determine correct login route
  //
  // Login routes by product and user type:
  // ┌─────────────────────────────────────────────────────────────────────────┐
  // │ /login                  - ADHD Assessment providers                     │
  // │ /rms/provider-sign-in   - RMS providers (RMS Clinician)                │
  // │ /service-user/login     - ADHD Assessment service users                │
  // │ /rms/school-sign-in     - RMS SENCOs and class teachers: password      │
  // │ /rms/family/sign-in     - RMS parents and carers: emailed sign-in link │
  // │ /rms/sign-in            - RMS service users (emailed sign-in link)     │
  // └─────────────────────────────────────────────────────────────────────────┘
  //
  // Note: RMS users have distinct accountTypes:
  //   - 'RMS Provider User' (with userType 'RMS Clinician'/'RMS Support'/'RMS Org. Admin')
  //   - 'RMS Service User' (with userType 'RMS School Representative'/'RMS Legal Guardian')
  const getLoginRoute = () => {
    // RMS routes: distinguish between service user and provider pages
    if (location.pathname?.startsWith('/rms')) {
      if (location.pathname.startsWith('/rms/provider')) {
        return '/rms/provider-sign-in'; // RMS Clinicians use RMS provider login
      }
      if (location.pathname.startsWith('/rms/family/')) {
        // Parents and carers sign in on the family portal, which returns them
        // to the page their email link pointed at.
        const back = `${location.pathname}${location.search || ''}`;
        return `/rms/family/sign-in?continue=${encodeURIComponent(back)}`;
      }
      if (location.pathname.startsWith('/rms/pupils') || location.pathname.startsWith('/rms/teacher')) {
        // SENCOs and class teachers (TDI-619) hold a password, so an expired
        // session or a bookmark has to land on the password page —
        // /rms/sign-in can only send an email link, which a school account
        // never receives.
        const back = `${location.pathname}${location.search || ''}`;
        return `/rms/school-sign-in?continue=${encodeURIComponent(back)}`;
      }
      // Everything else under /rms keeps the email-link page. That includes
      // /rms/patient/..., which both guardians and school staff use.
      return '/rms/sign-in'; // RMS service users
    }
    
    // If we have claims, use them to determine the route
    if (claims?.accountType) {
      if (claims.accountType === 'RMS Service User') return '/rms/sign-in';
      if (claims.accountType === 'RMS Provider User') return '/rms/provider-sign-in';
      if (claims.accountType === 'Service User') return '/service-user/login';
      // All other providers (Assessment) use standard provider login
      return '/login';
    }
    
    // If no claims but we have allowedRoles, use them to determine route
    if (allowedRoles) {
      // RMS service user roles → RMS sign-in
      if (allowedRoles.some(role => RMS_SERVICE_USER_TYPES.includes(role))) {
        return '/rms/sign-in';
      }
      // RMS provider roles → RMS provider sign-in
      if (allowedRoles.some(role => ['RMS Clinician'].includes(role))) {
        return '/rms/provider-sign-in';
      }
      // ADHD Assessment service user roles → patient login
      if (allowedRoles.some(role => ['Service User', 'Patient', 'Informant'].includes(role))) {
        return '/service-user/login';
      }
      // ADHD Assessment provider roles → provider login
      if (allowedRoles.some(role => ['Provider', 'Clinician', 'Support', 'Org. Admin'].includes(role))) {
        return '/login';
      }
    }
    
    // Default to provider login if we can't determine
    return '/login';
  };

  // Authenticated landing page for the user's product. Used when a logged-in
  // user reaches a route their role can't access, so we send them to their own
  // dashboard rather than the sign-in page (which looks like a forced logout —
  // TD-36). Each of these dashboards admits all roles of its product, so this
  // can't cause a redirect loop. Returns null when the home can't be resolved.
  const getHomeRoute = () => {
    switch (claims?.accountType) {
      case 'RMS Provider User': return '/rms/provider/dashboard';
      case 'RMS Service User': return '/rms/dashboard';
      case 'Provider':
      case 'Provider Account': return '/dashboard';
      default: return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    // console.warn('[TD-SSO] ProtectedRoute: auth error state', {
    //   pathname: location?.pathname,
    //   errorType,
    //   // Log the full error string for maximum debugging detail (per request).
    //   // This may include internal wording from Firebase/SDK; it should not include tokens/secrets.
    //   errorFull: typeof error === 'string' ? error : String(error),
    //   accountType: claims?.accountType,
    //   userType: claims?.userType,
    //   authMode: claims?.authMode,
    // });
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
          <div className="text-center">
            {errorType === 'temporary' ? (
              <>
                <div className="w-12 h-12 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Connection Issues</h3>
                <p className="text-gray-600 mb-4">
                  We're having trouble connecting to our servers. This is usually temporary.
                </p>
                <div className="space-y-2">
                  <button
                    onClick={retryAuth}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  >
                    Try Again
                  </button>
                  {/* <button
                    onClick={clearError}
                    className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                  >
                    Continue with Limited Features
                  </button> */}
                </div>
              </>
            ) : (
              <>
                <div className="w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Authentication Error</h3>
                <p className="text-gray-600 mb-4">
                  There was a problem with your authentication. Please sign in again.
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => window.location.reload()}
                    className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  >
                    Refresh Page
                  </button>
                  <button
                    onClick={() => window.location.href = getLoginRoute()}
                    className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                  >
                    Sign In Again
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    console.log('[TD-SSO] ProtectedRoute: not authenticated -> login', {
      pathname: location?.pathname,
      allowedRoles,
      inferredLoginRoute: getLoginRoute(),
    });
    const loginRoute = getLoginRoute();
    // Redirect to appropriate login page with return URL
    return <Navigate to={loginRoute} state={{ from: location }} replace />;
  }

  // Check if user has required role
  //
  // We have two products with a unified user taxonomy:
  //
  // ┌─────────────────────────────────────────────────────────────────────────┐
  // │ ACCOUNT TYPES (shared across products)                                  │
  // │ ─────────────────────────────────────────────────────────────────────── │
  // │ accountType='Service User'                                              │
  // │   ADHD Assessment: userType='Patient', 'Informant'                      │
  // │   RMS:             userType='RMS School Representative', 'RMS Legal Guardian' │
  // │                    (also has rms=true claim)                            │
  // │                                                                         │
  // │ accountType='Provider'                                                  │
  // │   ADHD Assessment: userType='Clinician', 'Org. Admin', 'Support'        │
  // │   RMS:             userType='RMS Clinician'                             │
  // └─────────────────────────────────────────────────────────────────────────┘
  //
  // RMS Service User Fast-Path:
  // For RMS service user routes (non-provider), we allow through if authenticated
  // and claims may not yet be populated (passwordless invite flows may lag).
  // However, RMS provider routes (/rms/provider/*) require explicit role checks.
  const isRmsServiceUserRoute = location.pathname?.startsWith('/rms') && 
                                 !location.pathname?.startsWith('/rms/provider');
  if (isAuthenticated && isRmsServiceUserRoute) {
    const elsewhere = rmsServiceUserRedirect(location.pathname, claims?.userType);
    if (elsewhere) return <Navigate to={elsewhere} replace />;
    console.log('[TD-SSO] ProtectedRoute: RMS service-user fast-path allow', { pathname: location.pathname });
    return children;
  }

  const hasRequiredRole = allowedRoles.some(role => {
    switch (role) {
      // ─── ADHD Assessment App: Provider Roles ─────────────────────────────────
      case 'Provider':
        // Generic provider role (ADHD app only - Clinician, Org. Admin, Support)
        return (claims?.accountType === 'Provider' || claims?.accountType === 'Provider Account') &&
               // Exclude RMS Clinician from generic 'Provider' role to maintain separation
               claims?.userType !== 'RMS Clinician';
      case 'Org. Admin':
        return (claims?.accountType === 'Provider' || claims?.accountType === 'Provider Account') && 
               claims?.userType === 'Org. Admin';
      case 'Clinician':
        // ADHD Assessment clinicians only (not RMS Clinician)
        return (claims?.accountType === 'Provider' || claims?.accountType === 'Provider Account') && 
               claims?.userType === 'Clinician';
      case 'Support':
        return (claims?.accountType === 'Provider' || claims?.accountType === 'Provider Account') && 
               claims?.userType === 'Support';

      // ─── ADHD Assessment App: Service User Roles ─────────────────────────────
      case 'Service User':
        // Assessment service users only (Patient, Informant)
        return claims?.accountType === 'Service User';
      case 'Patient':
        return claims?.accountType === 'Service User' && claims?.userType === 'Patient';
      case 'Informant':
        return claims?.accountType === 'Service User' && claims?.userType === 'Informant';

      // ─── RMS: Provider Roles ─────────────────────────────────────────────────
      case 'RMS Clinician':
        return claims?.accountType === 'RMS Provider User' && 
               claims?.userType === 'RMS Clinician';
      case 'RMS Support':
        return claims?.accountType === 'RMS Provider User' && 
               claims?.userType === 'RMS Support';
      case 'RMS Org. Admin':
        return claims?.accountType === 'RMS Provider User' && 
               claims?.userType === 'RMS Org. Admin';

      // ─── RMS: Service User Roles ─────────────────────────────────────────────
      case 'RMS School Representative':
        return claims?.accountType === 'RMS Service User' && 
               claims?.userType === 'RMS School Representative';
      case 'RMS Legal Guardian':
        return claims?.accountType === 'RMS Service User' &&
               claims?.userType === 'RMS Legal Guardian';
      case 'RMS Teacher':
        // TDI-489: class teachers completing their allocated school forms.
        return claims?.accountType === 'RMS Service User' &&
               claims?.userType === 'RMS Teacher';

      default:
        return false;
    }
  });

  if (!hasRequiredRole) {
    // The user IS authenticated (we passed the !isAuthenticated check above) but
    // lacks the role for this route. Send them to their own dashboard rather than
    // the sign-in page, which looks like a forced logout (TD-36). Fall back to the
    // login route only when we can't resolve a home for their account type.
    const homeRoute = getHomeRoute();
    const loginRoute = getLoginRoute();
    const target = homeRoute && homeRoute !== location.pathname ? homeRoute : loginRoute;
    console.warn('[TD-SSO] ProtectedRoute: missing required role -> redirect', {
      pathname: location?.pathname,
      allowedRoles,
      accountType: claims?.accountType,
      userType: claims?.userType,
      authMode: claims?.authMode,
      target,
    });
    return <Navigate to={target} state={{ from: location }} replace />;
  }

  // For Provider roles (both Assessment and RMS), check email verification and MFA
  if (claims?.accountType === 'Provider' || claims?.accountType === 'RMS Provider User') {
    const isMicrosoftProvider = Boolean(user?.providerData?.some((p) => p?.providerId === 'microsoft.com'));
    const isMicrosoftFirstAccount = claims?.authMode === 'with_microsoft_sso';

    // Check email verification first
    //
    // Microsoft sign-in already asserts ownership of the email. For Microsoft-first accounts,
    // Firebase's `emailVerified` flag can lag (especially immediately after first SSO login)
    // even though the IdP has verified the identity.
    //
    // Business rule:
    // - For password/hybrid accounts: keep existing email verification gate.
    // - For Microsoft-first accounts (or Microsoft provider sessions): skip this gate.
    if (!isMicrosoftFirstAccount && !isMicrosoftProvider && !isEmailVerified) {
      console.warn('[TD-SSO] ProtectedRoute: Provider email not verified -> /verify-email', {
        pathname: location?.pathname,
        userType: claims?.userType,
        authMode: claims?.authMode,
      });
      return <Navigate to="/verify-email" state={{ from: location }} replace />;
    }

    // Special case: don't check MFA requirement for the MFA setup page
    const isMFASetupPage = location.pathname === '/set-up-mfa';
    
    // For all other protected Provider routes, check if MFA is set up.
    //
    // Microsoft-first organisations (e.g. SLaM) typically enforce MFA via Microsoft Entra policies.
    // Requiring additional Firebase MFA would create a confusing double-MFA user journey
    // and significantly slow down controlled pilots.
    //
    // Business rule:
    // - Default behaviour: Provider users must enroll MFA (SMS or authenticator app / TOTP).
    //   The method choice is made on the SetUpMFA page; this gate is method-agnostic
    //   (isMFAEnrolled checks enrolledFactors.length > 0 regardless of factor type).
    // - If the account is configured as 'with_microsoft_sso', we skip the Firebase MFA gate.
    if (!isMicrosoftFirstAccount && !isMFASetupPage && !isMFAEnrolled) {
      console.warn('[TD-SSO] ProtectedRoute: Provider MFA not enrolled -> /set-up-mfa', {
        pathname: location?.pathname,
        userType: claims?.userType,
        authMode: claims?.authMode,
        isMFASetupPage,
      });
      return <Navigate to="/set-up-mfa" state={{ from: location }} replace />;
    }
  }

  // console.log('[TD-SSO] ProtectedRoute: allow', {
  //   pathname: location?.pathname,
  //   accountType: claims?.accountType,
  //   userType: claims?.userType,
  //   authMode: claims?.authMode,
  // });
  return children;
}